import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { WORK_MEDIA } from "./media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentCtx = createContext(null);

// Convert flat pullQuote{Text,Author} to nested pullQuote and merge media for projects.
const enrichWork = (work) =>
  (work || []).map((w) => {
    const media = WORK_MEDIA[w.id] || {};
    const pullQuote =
      w.pullQuoteText && w.pullQuoteText.trim()
        ? { text: w.pullQuoteText, author: w.pullQuoteAuthor || "" }
        : null;
    return { ...media, ...w, pullQuote };
  });

const flattenWorkForApi = (work) =>
  (work || []).map((w) => ({
    id: w.id,
    index: w.index,
    title: w.title,
    subtitle: w.subtitle,
    role: w.role,
    year: w.year,
    tagline: w.tagline,
    preview: w.preview,
    summary: w.summary,
    details: w.details,
    pullQuoteText: w.pullQuote?.text || "",
    pullQuoteAuthor: w.pullQuote?.author || "",
  }));

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContent({ ...data, work: enrichWork(data.work) });
    } catch (e) {
      console.error("Failed to load content:", e);
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateSection = useCallback(
    async (section, value, token) => {
      const payload = section === "work" ? flattenWorkForApi(value) : value;
      await axios.put(
        `${API}/content/section`,
        { section, value: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refresh();
    },
    [refresh]
  );

  return (
    <ContentCtx.Provider value={{ content, loading, refresh, updateSection }}>
      {children}
    </ContentCtx.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentCtx);
  if (!ctx) throw new Error("useContent must be used inside ContentProvider");
  return ctx;
};
